import { useState } from "react";
import { useTranslate } from "@refinedev/core";
import { Flex } from "antd";
import styled from "styled-components";
import { Slider } from "@libertify/ui";
import { useAntTheme } from "hooks/useAntTheme";
import { useTeacherOnboarding } from "./teacherOnboardingContext";
import { useOnboardingQuestionnaire } from "../creator-profile/hooks/useOnboardingQuestionnaire";
import StepHeader from "./StepHeader";
import { I18N_PREFIX } from "./constants";
import type { TeacherAnswers, TeacherStep } from "./types";

// depth / autonomy share the same slider with two labelled ends.
const SLIDER_FIELD: Partial<Record<TeacherStep, keyof TeacherAnswers>> = {
  depth: "depth",
  autonomy: "autonomy",
};

const SliderStep = () => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const { currentStep, answers, stageAnswer } = useTeacherOnboarding();
  // GET is de-duplicated by React Query, so calling this per step is cheap.
  const { template: questionnaire } = useOnboardingQuestionnaire("teacher");
  const template =
    questionnaire?.type === "teacher" ? questionnaire : undefined;

  const field = SLIDER_FIELD[currentStep];
  const [value, setValue] = useState<number>(
    field ? (answers[field] as number) : 50
  );

  if (!field) return null;

  // Slider bounds come from the template when available (falls back to 0–100).
  const sliderQuestion =
    field === "depth" || field === "autonomy"
      ? template?.[field]
      : undefined;
  const min = sliderQuestion?.min_value ?? 0;
  const max = sliderQuestion?.max_value ?? 100;
  const step = sliderQuestion?.step ?? 1;

  return (
    <Flex vertical align="center" gap={28} style={{ width: "100%" }}>
      <StepHeader i18nBase={currentStep} />

      <Flex vertical gap={4} style={{ width: "100%", maxWidth: 560 }}>
        <Flex justify="space-between" gap={16}>
          <End>
            <b>{t(`${I18N_PREFIX}.${currentStep}.low.label`)}</b>
            <span>{t(`${I18N_PREFIX}.${currentStep}.low.quote`)}</span>
          </End>
          <End $end>
            <b>{t(`${I18N_PREFIX}.${currentStep}.high.label`)}</b>
            <span>{t(`${I18N_PREFIX}.${currentStep}.high.quote`)}</span>
          </End>
        </Flex>

        <Slider
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(v) => setValue(v)}
          onChangeComplete={(v) => stageAnswer({ [field]: v })}
          tooltip={{ open: false }}
        />
        <Flex
          justify="space-between"
          style={{
            padding: "0 3px",
            fontSize: 10.5,
            color: theme.colorTextTertiary,
          }}
        >
          {[0, 25, 50, 75, 100].map((tick) => (
            <span key={tick}>|</span>
          ))}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default SliderStep;

// ─── Styled components ───

const End = styled.div<{ $end?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-width: 46%;
  text-align: ${({ $end }) => ($end ? "right" : "left")};

  b {
    font-size: 14px;
    color: ${({ theme }) => theme.colorText};
  }
  span {
    font-size: 12.5px;
    color: ${({ theme }) => theme.colorTextSecondary};
  }
`;
